import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  inject,
  computed,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Category } from '@pedix-workspace/utils';
import { ScrollPositionService } from '@pedix-workspace/angular-utils';
import { EstablishmentWorkingHoursService } from '../../main/establishment-working-hours.service';
import {
  ActiveCategoriesPipe,
  IsCategoryAvailablePipe,
  getUniqueTagsWithQuantities,
} from '@pedix-workspace/pedixapp-shared-pipes';
import { ShowOrderComponent } from '../show-order/show-order.component';
import { IconBackComponent, IconFilterComponent } from '@pedix-workspace/angular-ui-icons';
import { CategoryListItemComponent } from '@pedix-workspace/pedixapp-shared-category-list-item';
import { AsyncPipe } from '@angular/common';
import { SeoService } from '../../main/seo.service';
import { AppRequestContextService } from '../../../app-request-context.service';
import { TranslocoDirective } from '@jsverse/transloco';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { ProductFiltersDialogComponent } from '../category/product-filters/product-filters-dialog.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CategoryAvailabilityDialogComponent } from '@pedix-workspace/pedixapp-shared-catalog';
import { PromotionsService } from '../../../promotions.service';

@Component({
  selector: 'app-categories',
  templateUrl: './categories.component.html',
  styleUrls: ['./categories.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    CategoryListItemComponent,
    ShowOrderComponent,
    CategoryAvailabilityDialogComponent,
    AsyncPipe,
    IsCategoryAvailablePipe,
    ActiveCategoriesPipe,
    ButtonComponent,
    IconFilterComponent,
    ProductFiltersDialogComponent,
    IconBackComponent,
    RouterLink,
  ],
})
export class CategoriesComponent implements OnInit, AfterViewInit {
  private seoService = inject(SeoService);
  private scrollPosition = inject(ScrollPositionService);
  private appRequestContext = inject(AppRequestContextService);
  private establishmentWorkingHours = inject(EstablishmentWorkingHoursService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private promotionsService = inject(PromotionsService);

  readonly categoryListElement = viewChild.required<ElementRef>('categoryList');
  readonly categoryAvailability = viewChild.required(CategoryAvailabilityDialogComponent);
  readonly filtersDialog = viewChild(ProductFiltersDialogComponent);

  readonly openedStatus = toSignal(this.establishmentWorkingHours.openedStatus$);
  readonly isCartDisabled = computed(() => this.openedStatus()?.isCartDisabled ?? false);

  readonly establishment = computed(() => this.appRequestContext.establishment());
  readonly selectedLanguage = computed(() => this.appRequestContext.selectedLanguage());
  readonly categories = computed(() => this.appRequestContext.allCategories());
  readonly products = computed(() => this.appRequestContext.activeProducts());
  readonly productTagsWithQuantities = computed(() =>
    this.establishment().features.includes('PRODUCT_TAGS')
      ? getUniqueTagsWithQuantities(this.products())
      : [],
  );
  readonly categoriesWithPromotions = computed(() => {
    const categories = this.categories();
    const set = new Set<string>();
    for (const category of categories) {
      if (this.promotionsService.hasPromotionsInCategory(category.id)) {
        set.add(category.id);
      }
    }
    return set;
  });
  readonly subsidiarySelector = computed(() => this.appRequestContext.subsidiarySelector());

  readonly categoryMaxDiscounts = computed(() => {
    const categories = this.categories();
    const map = new Map<string, number>();

    for (const category of categories) {
      const maxDiscount = this.promotionsService.getMaxHeadlineDiscountForCategory(category.id);
      if (maxDiscount > 0) {
        map.set(category.id, maxDiscount);
      }
    }

    return map;
  });

  ngOnInit() {
    this.seoService.setEstablishmentMetadata(this.establishment());
  }

  ngAfterViewInit() {
    this.categoryListElement().nativeElement.scrollTop = this.scrollPosition.get(
      'categories',
      this.establishment().slug,
    );
  }

  onScroll() {
    this.scrollPosition.set(
      'categories',
      this.categoryListElement().nativeElement.scrollTop,
      this.establishment().slug,
    );
  }

  displayAvailabilityForCategory(category: Pick<Category, 'availability'>) {
    this.categoryAvailability().open(category);
  }

  onFilterProducts() {
    this.filtersDialog()?.open();
  }

  onSelectedTags(selectedTags: string[]) {
    this.router.navigate(['/busqueda'], {
      relativeTo: this.activatedRoute,
      queryParams: {
        qt: selectedTags.join(','),
      },
    });
  }
}
